import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiArrowLeft, FiSearch, FiUser, FiX } from "react-icons/fi";
import RightWrapper from "../../../Components/CommonComponents/ComponentWrapper";
import CustomInput from "../../../Components/CommonComponents/CustomInput";
import CustomSelect from "../../../Components/CommonComponents/CustomSelect";
import CustomTextarea from "../../../Components/CommonComponents/CustomTextarea";
import { apiGet } from "../../../Components/CommonComponents/tableApi";
import { END_POINT, getJwtToken } from "../../../Redux/AdminReducer/action";
import { consultantTypes } from "../../../Files/dropdownOptions";
import { useCheckRole } from "../../../UtilityHookFunctions/LoggedInUser";

// Clinical context the consultants bid against. Kept short on purpose: the
// doctor is filling this in during a consultation, not sitting the patient down
// with the full questionnaire.
const DETAIL_FIELDS = [
  {
    name: "chiefComplaint",
    label: "Reason for referral",
    placeholder: "Why this patient needs the therapy",
    required: true,
  },
  { name: "medicalHistory", label: "Relevant medical history", placeholder: "Conditions, surgeries, allergies" },
  { name: "currentMedication", label: "Current medication", placeholder: "Ongoing prescriptions and supplements" },
  { name: "treatmentGoal", label: "Treatment goal", placeholder: "What a successful outcome looks like" },
];

const patientLabel = (p) =>
  `${p?.firstName || ""} ${p?.surname || ""}`.trim() || p?.email || p?.mobile || "Unnamed patient";

/** Search-as-you-type picker over the patient list. */
const PatientPicker = ({ patient, onSelect, onClear, error }) => {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (patient) return;
    const query = term.trim();
    setIsSearching(true);
    const timer = setTimeout(() => {
      apiGet(`customer/list/dashboard?search=${encodeURIComponent(query)}&page=1&limit=10`)
        .then((data) => setResults(Array.isArray(data) ? data : data?.data || []))
        .finally(() => setIsSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [term, patient]);

  // Close the result list when the click lands outside it.
  useEffect(() => {
    const onClickAway = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  if (patient) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
          <FiUser className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">{patientLabel(patient)}</p>
          <p className="truncate text-xs text-slate-500">
            {[patient?.mobile, patient?.email, patient?.customerId].filter(Boolean).join(" · ") || "No contact details"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          aria-label="Choose a different patient"
          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
        >
          <FiX className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search by name, phone or email"
          className={`w-full rounded-md border bg-white py-2 pl-9 pr-3 text-xs transition focus:outline-none focus:ring-2 focus:ring-sky-500 md:text-sm ${
            error ? "border-red-400 focus:ring-red-300" : "border-gray-300 focus:border-sky-500"
          }`}
        />
      </div>

      {isOpen && (
        <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          {isSearching && <li className="px-3 py-2 text-xs text-slate-400">Searching…</li>}
          {!isSearching && results.length === 0 && (
            <li className="px-3 py-2 text-xs text-slate-400">No patients found.</li>
          )}
          {!isSearching &&
            results.map((p) => (
              <li key={p._id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(p);
                    setIsOpen(false);
                    setTerm("");
                  }}
                  className="block w-full px-3 py-2 text-left transition hover:bg-slate-50"
                >
                  <span className="block truncate text-sm font-medium text-slate-800">{patientLabel(p)}</span>
                  <span className="block truncate text-xs text-slate-500">
                    {[p?.mobile, p?.email].filter(Boolean).join(" · ") || "No contact details"}
                  </span>
                </button>
              </li>
            ))}
        </ul>
      )}

      {error && <span className="mt-1 block text-xs text-red-500">{error}</span>}
    </div>
  );
};

export default function CreateTherapyRequest() {
  const navigate = useNavigate();
  const checkrole = useCheckRole();
  const isMainDoctor = checkrole("maindoctor");

  const [patient, setPatient] = useState(null);
  const [therapy, setTherapy] = useState("");
  const [details, setDetails] = useState({});
  const [openForBidding, setOpenForBidding] = useState(true);
  const [duration, setDuration] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const therapyOptions = useMemo(() => consultantTypes, []);

  const validate = () => {
    const next = {};
    if (!patient?._id) next.patient = "Please choose a patient";
    if (!therapy) next.therapy = "Please choose a therapy";
    if (!String(details.chiefComplaint || "").trim()) next.chiefComplaint = "Reason for referral is required";
    if (openForBidding && String(duration).trim() !== "" && Number(duration) <= 0) {
      next.duration = "Duration must be more than 0 days";
    }
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      toast.error("Please fill the highlighted fields");
      return;
    }
    setErrors({});

    // The questionnaire keys the patient-facing form uses, so this request reads
    // the same way on the request page and on the patient's Diet tab.
    const requestForm = {
      patientId: patient._id,
      fullName: patientLabel(patient),
      mobile: patient?.mobile || "",
      gender: patient?.gender || "",
      dateOfBirth: patient?.dateOfBirth || "",
      ...Object.entries(details).reduce((acc, [key, value]) => {
        const trimmed = typeof value === "string" ? value.trim() : value;
        if (trimmed) acc[key] = trimmed;
        return acc;
      }, {}),
    };

    setIsProcessing(true);
    try {
      const { data } = await axios.post(
        `${END_POINT}/alternative_therapy/staff/create/${therapy}`,
        { requestForm, openForBidding, duration, note: note.trim() },
        { headers: { Authorization: getJwtToken() } }
      );
      toast.success(openForBidding ? "Request created and opened for bidding" : "Request created");
      navigate(data?._id ? `/Bids/${data._id}` : "/AlternativeTherapies");
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.response?.data || "Something went wrong");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isMainDoctor) {
    return (
      <RightWrapper>
        <div className="w-full px-2 py-4 md:px-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800">
            Only the main doctor can raise a therapy request.
          </div>
        </div>
      </RightWrapper>
    );
  }

  return (
    <RightWrapper>
      <div className="w-full px-2 py-4 md:px-4">
        {/* Page header */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
          >
            <FiArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">New Therapy Request</h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Raise a request for a patient who has not filled the questionnaire, and send it straight to bidding.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Patient & therapy */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-sm font-semibold tracking-tight text-slate-900">Patient &amp; Therapy</h2>
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 px-6 py-5 lg:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 md:text-sm">Patient *</label>
                <PatientPicker
                  patient={patient}
                  onSelect={(p) => {
                    setPatient(p);
                    setErrors((prev) => ({ ...prev, patient: "" }));
                  }}
                  onClear={() => setPatient(null)}
                  error={errors.patient}
                />
              </div>
              <CustomSelect
                label="Therapy *"
                name="therapy"
                value={therapy}
                options={therapyOptions}
                placeholder="-- Select Therapy --"
                error={errors.therapy}
                style={{ textTransform: "capitalize" }}
                onChange={(e) => {
                  setTherapy(e.target.value);
                  setErrors((prev) => ({ ...prev, therapy: "" }));
                }}
              />
            </div>
          </section>

          {/* Clinical details */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-sm font-semibold tracking-tight text-slate-900">Request Details</h2>
              <p className="mt-0.5 text-xs text-slate-500">Consultants see these details when they bid.</p>
            </div>
            <div className="grid grid-cols-1 gap-x-6 px-6 pb-5 lg:grid-cols-2">
              {DETAIL_FIELDS.map((field) => (
                <CustomTextarea
                  key={field.name}
                  name={field.name}
                  required={false}
                  label={`${field.label}${field.required ? " *" : ""}`}
                  placeholder={field.placeholder}
                  value={details[field.name] || ""}
                  error={errors[field.name]}
                  onChange={(e) => {
                    setDetails((prev) => ({ ...prev, [field.name]: e.target.value }));
                    setErrors((prev) => ({ ...prev, [field.name]: "" }));
                  }}
                />
              ))}
            </div>
          </section>

          {/* Bidding */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-sm font-semibold tracking-tight text-slate-900">Bidding</h2>
            </div>
            <div className="px-6 py-5">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={openForBidding}
                  onChange={(e) => setOpenForBidding(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-sky-600"
                />
                <span>
                  <span className="block text-sm font-medium text-slate-800">Open for bidding straight away</span>
                  <span className="block text-xs text-slate-500">
                    Consultants of this therapy type can bid immediately. Leave unticked to save it as a new request you
                    can open later.
                  </span>
                </span>
              </label>

              {openForBidding && (
                <div className="mt-5 max-w-md">
                  <CustomInput
                    label="Expected duration (days)"
                    name="duration"
                    type="number"
                    value={duration}
                    error={errors.duration}
                    placeholder="e.g. 30"
                    onChange={(e) => {
                      setDuration(e.target.value);
                      setErrors((prev) => ({ ...prev, duration: "" }));
                    }}
                  />
                  <CustomTextarea
                    label="Note for consultants"
                    name="note"
                    required={false}
                    value={note}
                    placeholder="Anything the bidding consultants should know"
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              )}
            </div>
          </section>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pb-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="rounded-xl bg-sky-600 px-6 py-2 text-sm font-semibold text-white shadow-sm shadow-sky-600/30 transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isProcessing ? "Please wait..." : openForBidding ? "Create & Open for Bidding" : "Create Request"}
            </button>
          </div>
        </form>
      </div>
    </RightWrapper>
  );
}
